import type {
  ExperienceItem,
  SkillGroup,
  Project,
  Stat,
  SocialLink,
} from "../types";

const CITY = "Hobart";
const STATE = "TAS";
const COUNTRY = "Australia";

// ─── Personal info ────────────────────────────────────────────────────────────
export const PERSONAL = {
  name: "Don Jayasinghe",
  title: "Full Stack Software Engineer",
  tagline:
    "Full Stack Software Engineer with 4+ years delivering reliable, scalable applications " +
    "across energy, industrial, and tech domains — specialising in React, TypeScript, and " +
    "cloud-native architecture.",
  badge: `Available for new roles · ${CITY}, ${COUNTRY}`,
  email: "manojjayasinghe43@gmail.com",
  phone: "+61 478 005 616",
  location: `${CITY}, ${STATE}, ${COUNTRY}`,
} as const;

// ─── Stats ────────────────────────────────────────────────────────────────────
export const STATS: Stat[] = [
  { num: "4+", label: "Years Experience" },
  { num: "6", label: "Projects Shipped" },
  { num: "M.IT", label: "RMIT University" },
  { num: "AU", label: `Based in ${CITY}, ${COUNTRY}` },
];

// ─── Social links ─────────────────────────────────────────────────────────────
export const SOCIAL_LINKS: SocialLink[] = [
  {
    name: "LinkedIn",
    handle: "manoj-jayasinghe-76232a15a",
    icon: "in",
    href: "https://www.linkedin.com/in/manoj-jayasinghe-76232a15a/",
  },
  {
    name: "GitHub",
    handle: "donroyj95",
    icon: "gh",
    href: "https://github.com/donroyj95",
  },
  {
    name: "Phone",
    handle: "+61 478 005 616",
    icon: "☎",
    href: "tel:+61478005616",
  },
];

// ─── Experience ───────────────────────────────────────────────────────────────
export const EXPERIENCE: ExperienceItem[] = [
  {
    company: "Esdan Plastics",
    period: "Oct 2025 – Mar 2026",
    role: "Software Developer",
    at: "Esdan Plastics Pty Ltd",
    loc: "Melbourne, VIC, Australia",
    bullets: [
      "Developed and maintained the company website using React and JavaScript, improving online presence for a Melbourne-based plastics manufacturing business.",
      "Built internal web tools to digitalise production and warehouse workflows, reducing manual data entry and improving day-to-day operational efficiency.",
      "Worked directly with operations and warehouse staff to gather requirements and translate business needs into practical software solutions.",
      "Integrated data capture systems to track inventory, orders and production output in real time.",
      "Ensured consistent performance and usability across all delivered tools through ongoing maintenance and bug fixes.",
    ],
    stack: [
      "React",
      "TypeScript",
      "HTML5",
      "CSS3",
      "TailwindCSS",
      "Node.js",
      "Express.js",
      "MySQL",
      "REST APIs",
      "AWS (EC2, S3, RDS)",
      "Git",
    ],
  },
  {
    company: "Aviation Academy",
    period: "Jul 2025 – Oct 2025",
    role: "Software Engineer Intern",
    at: "Aviation Academy",
    loc: "Melbourne, VIC, Australia",
    bullets: [
      "Built a full-stack Flight-School Dashboard centralising student progress and training data with real-time analytics.",
      "Implemented secure data handling, media uploads, and role-based permissions.",
      "Delivered a responsive, data-driven platform enhancing visibility across flight schools.",
    ],
    stack: [
      "React",
      "Next.js",
      "TypeScript",
      "Node.js",
      "MongoDB",
      "Tailwind CSS",
      "Jest",
    ],
  },
  {
    company: "Creative Software",
    period: "Jul 2021 – Aug 2024",
    role: "Software Engineer",
    at: "Creative Software — COGNITE",
    loc: "Colombo, Sri Lanka",
    bullets: [
      "Delivered enterprise-grade Data Explorer & Discover applications for COGNITE, enabling engineers to access P&IDs, 3D models, and drilling data.",
      "Engineered feature development aligned with product roadmaps for global industrial end-users.",
      "Expanded test coverage using Jest, Cypress, TestCafe, and MSW to minimise defects and accelerate releases.",
      "Collaborated in Agile cross-functional teams leveraging Kanban, Jira, and GitHub Actions for CI/CD.",
    ],
    stack: [
      "React.js",
      "TypeScript",
      "Next.js",
      "Redux",
      "Jenkins",
      "Terraform",
      "Azure",
      "Micro-Frontends",
      "Vite",
    ],
  },
  {
    company: "Creative Software",
    period: "Feb 2020 – Aug 2020",
    role: "Software Engineer Intern",
    at: "Creative Software",
    loc: "Colombo, Sri Lanka",
    bullets: [
      "Designed and implemented a question bank and assessment module within TrackIt (Employee Management System).",
      "Enabled interviewers to create, manage, and share candidate tests seamlessly via email.",
    ],
    stack: [
      "Angular",
      "TypeScript",
      "Spring Boot",
      "PostgreSQL",
      "Agile",
      "Jira",
    ],
  },
];

// ─── Skills ───────────────────────────────────────────────────────────────────
export const SKILLS: SkillGroup[] = [
  {
    title: "Languages",
    items: [
      "JavaScript",
      "TypeScript",
      "Java",
      "Python",
      "C++",
      "C#",
      "PHP",
      "HTML5",
      "CSS3",
    ],
  },
  {
    title: "Frameworks & Libraries",
    items: [
      "React",
      "Redux",
      "Angular",
      "Next.js",
      "Node.js",
      "Spring Boot",
      "GraphQL",
      "Express.js",
      "React Native",
      "TailwindCSS",
    ],
  },
  {
    title: "Cloud & DevOps",
    items: [
      "AWS",
      "Terraform",
      "Docker",
      "Kubernetes",
      "Jenkins",
      "GitHub Actions",
      "Linux",
      "CI/CD",
      "Serverless",
    ],
  },
  {
    title: "Databases",
    items: ["MySQL", "PostgreSQL", "SQLite", "MongoDB", "Data Modeling", "ETL"],
  },
  {
    title: "Testing",
    items: [
      "Cypress",
      "Jest",
      "Playwright",
      "Postman",
      "Unit Testing",
      "Load Testing",
    ],
  },
  {
    title: "AI & Emerging Tech",
    items: [
      "LLM Integration",
      "Prompt Engineering",
      "AWS SageMaker",
      "OpenAI",
      "Google AI",
    ],
  },
];

// ─── Projects ─────────────────────────────────────────────────────────────────
export const PROJECTS: Project[] = [
  {
    num: "01",
    title: "Secure E-Commerce Platform",
    desc: "Architected a secure, scalable e-commerce platform for 5–10K users with OWASP Top 10 threat modelling, TLS 1.3, WAF, and secure session management.",
    tags: ["React.js", "Node.js", "OWASP", "TLS 1.3", "Stripe/PayPal"],
  },
  {
    num: "02",
    title: "Flight-School Dashboard",
    desc: "Full-stack platform centralising student training progress for instructors — real-time analytics, role-based access, and secure media uploads.",
    tags: ["Next.js", "TypeScript", "MongoDB", "Node.js", "Tailwind CSS"],
  },
  {
    num: "03",
    title: "Web-Based Library System",
    desc: "Full-stack library management with role-based access, encrypted credentials, digital book downloads, and Diffie–Hellman key exchange.",
    tags: ["React.js", "Node.js", "AWS", "SQLite", "GraphQL"],
  },
  {
    num: "04",
    title: "AI-Powered Business Automation Platform",
    desc: "Designed and developed a full-stack web platform that integrates AI-driven automation to streamline business workflows and improve operational efficiency.",
    tags: [
      "React",
      "Node.js",
      "TypeScript",
      "OpenAI API",
      "REST APIs",
      "MongoDB",
      "AWS",
      "GitHub Actions",
      "TailwindCSS",
    ],
  },
  {
    num: "05",
    title: "COGNITE Data Explorer",
    desc: "Enterprise industrial data applications for engineers and geologists to access, visualise, and interpret P&IDs, 3D models, and drilling data.",
    tags: ["React.js", "TypeScript", "Redux", "Azure", "Micro-Frontends"],
  },
  {
    num: "06",
    title: "TrackIt Assessment Module",
    desc: "Question bank and assessment feature within an Employee Management System, letting interviewers create, manage, and share candidate tests.",
    tags: ["Angular", "TypeScript", "Spring Boot", "PostgreSQL"],
  },
];
